import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Logo } from '@/components/ui/Logo';
import { Menu, X } from 'lucide-react';
import { useEffect, useState } from 'react';

const navLinks = [
  { to: '/#features', label: 'Features' },
  { to: '/use-cases', label: 'Use Cases' },
  { to: '/about', label: 'About' },
  { to: '/pricing', label: 'Pricing' },
  { to: '/contact', label: 'Contact' },
];

export const PublicHeader = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      className={`sticky top-0 z-50 bg-midnight-green transition-shadow duration-300 ease-out ${
        scrolled
          ? 'shadow-[0_1px_0_0_hsl(var(--copper)/0.25)] border-b border-copper/15'
          : 'border-b border-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-14">
          <Link to="/" className="flex items-center transition-transform duration-200 ease-out hover:scale-[1.02]">
            <Logo variant="horizontal" size="md" className="brightness-0 invert" />
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => {
              const isActive = pathname === link.to.replace('/#', '/');
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`story-link font-inter text-sm font-medium tracking-tight transition-colors duration-200 ${
                    isActive ? 'text-copper' : 'text-ivory/85 hover:text-ivory'
                  }`}
                >
                  {link.label}
                </Link>
              );
            })}
          </nav>

          <div className="hidden md:flex items-center gap-2">
            <Button asChild variant="ghost" className="text-ivory/90 hover:text-ivory hover:bg-ivory/10 btn-press">
              <Link to="/auth">Sign in</Link>
            </Button>
            <Button asChild className="bg-copper hover:bg-copper/90 text-ivory font-medium btn-press">
              <Link to="/contact">Request demo</Link>
            </Button>
          </div>

          <button
            className="md:hidden text-ivory p-2 -mr-2"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Toggle navigation"
          >
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {mobileOpen && (
          <div className="md:hidden py-4 border-t border-copper/20 bg-midnight-green/95 backdrop-blur-xl">
            <nav className="flex flex-col">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="font-inter text-ivory/90 hover:text-copper transition-colors py-3 text-base"
                  onClick={() => setMobileOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              <div className="flex flex-col gap-2 pt-4 mt-2 border-t border-copper/20">
                <Button asChild variant="ghost" className="text-ivory hover:bg-ivory/10 justify-start">
                  <Link to="/auth" onClick={() => setMobileOpen(false)}>Sign in</Link>
                </Button>
                <Button asChild className="bg-copper hover:bg-copper/90 text-ivory">
                  <Link to="/contact" onClick={() => setMobileOpen(false)}>Request demo</Link>
                </Button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};
